import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Image } from '@realadvisor/image';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useEditableImagesContext } from '@modules/_shared/components/image-gallery/context/editableImagesContext';
import type { ImageGalleryItem } from '@modules/_shared/components/image-gallery/NewImageGallery';
import { Puller } from '@modules/_shared/components/puller';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

type Props = {
  selectedImageId?: string;
  onFullScreenClick?: (index: number) => void;
  ImageForm: React.FC<{ imageId: string }>;
};

export const ImageEditPanel = ({
  selectedImageId,
  onFullScreenClick,
  ImageForm,
}: Props) => {
  const navigate = useNavigate();

  const { t } = useLocale();
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('lg'));
  const { autoSave, canEditMeta, images } =
    useEditableImagesContext<ImageGalleryItem>();

  const currentImageIndex = images.findIndex(img => img.id === selectedImageId);
  const currentImage =
    currentImageIndex !== -1 ? images[currentImageIndex] : null;

  const handleClosePanel = () => {
    navigate('.', { replace: true });
  };

  const handleNavigate = (newIndex: number) => {
    if (newIndex < 0 || newIndex >= images.length) {
      return;
    }
    navigate(`./${images[newIndex].id}`, { replace: true });
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
      }}
    >
      {autoSave && !isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 1,
            px: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
            }}
          >
            {t('Photo')} {currentImageIndex + 1}
          </Typography>
          <IconButton onClick={handleClosePanel}>
            <X size={20} />
          </IconButton>
        </Box>
      )}
      <Box
        sx={{
          p: 2,
          pt: autoSave ? 1 : 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {currentImage?.image.url != null && (
            <Box
              sx={{
                position: 'relative',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                  background:
                    'linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 22%), linear-gradient(180deg, rgba(0, 0, 0, 0) 78.25%, rgba(0, 0, 0, 0.3) 100%)',
                }}
              />
              <IconButton
                sx={{
                  color: 'white.main',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  zIndex: 2,
                }}
                aria-label={t('Fullscreen')}
                onClick={() => onFullScreenClick?.(currentImageIndex)}
              >
                <Maximize2 size={20} />
              </IconButton>
              <Typography
                sx={{
                  color: 'white.main',
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  zIndex: 2,
                  lineHeight: 1,
                }}
              >
                {currentImageIndex + 1}/{images.length}
              </Typography>
              <Image
                src={currentImage.watermarked_url ?? currentImage.image.url}
                options={{ h: 480, f: 'jpg' }}
                objectFit="contain"
                css={{ maxWidth: '100%', height: '400px' }}
              />
            </Box>
          )}
        </Box>

        {currentImageIndex > -1 && (
          <Stack
            spacing={4}
            direction="row"
            sx={{
              justifyContent: 'space-between',
              marginTop: 2,
            }}
          >
            <Button
              size="small"
              startIcon={<ChevronLeft size={18} />}
              disabled={currentImageIndex === 0}
              onClick={() => handleNavigate(currentImageIndex - 1)}
            >
              {t('Previous')}
            </Button>
            <Button
              size="small"
              endIcon={<ChevronRight size={18} />}
              disabled={currentImageIndex === images.length - 1}
              onClick={() => handleNavigate(currentImageIndex + 1)}
            >
              {t('Next')}
            </Button>
          </Stack>
        )}
      </Box>
      {currentImage && canEditMeta && <ImageForm imageId={currentImage.id} />}
    </Box>
  );

  return isMobile ? (
    <SwipeableDrawer
      anchor="bottom"
      open={selectedImageId != null}
      onClose={handleClosePanel}
      onOpen={() => {}}
      sx={{
        '& > .MuiPaper-root': {
          height: '90%',
          overflow: 'hidden',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          bgcolor: customPalette.superLightBlue,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          padding: '8px 0',
          position: 'absolute',
          zIndex: 10,
          top: 0,
          left: 0,
          width: '100%',
          bgcolor: customPalette.superLightBlue,
        }}
      >
        <Puller />
      </Box>
      <Box
        sx={{
          height: '100%',
          overflow: 'auto',
          pt: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {drawerContent}
      </Box>
    </SwipeableDrawer>
  ) : (
    <Drawer
      variant="persistent"
      anchor="right"
      open
      sx={{
        width: '100%',
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: '100%',
          position: 'absolute',
          border: 0,
          bgcolor: customPalette.superLightBlue,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};
